import {loadWordsSentences} from '$lib/utils/loadWordsSentences';
import {allWords, setAllWords} from '$lib/data/allWords';
import {wordIdToWord, initWordIdToWord} from '$lib/data/wordIdToWord';
import {wordIdChanges, loadWordIdChanges} from '$lib/data/word-id-changes';
import {firebaseApp as FirebaseApp} from '$lib/classes/services/firebase.js';
import {Functions} from '$lib/classes/services/functions';
import {Database} from '$lib/classes/services/database';
import {Auth} from '$lib/classes/services/auth.js';
import type {User} from 'firebase/auth';
import {VerbConjugations} from '$lib/data/verb-conjugations';
import {ReviewCount} from '$lib/classes/stores/review-count.svelte';
import {FreeLimitReached} from '$lib/classes/state/free-limit-reached.svelte';
import {ProVersion} from '$lib/classes/stores/pro-version.svelte';
import {Subscription} from '$lib/classes/stores/subscription.svelte';
import {PaymentService} from '$lib/classes/services/payment-service.js';
import {timer} from '$lib/utils/timer.js';
import {logInProduction} from '$lib/utils/logInProduction.js';
import {browser, version} from '$app/environment';
import * as Sentry from '@sentry/sveltekit';
import * as SentryCapacitor from '@sentry/capacitor';
import {Interval as interval} from '$lib/utils/userWords/interval';
import {UserSettings} from '$lib/classes/stores/user-settings.svelte';
import {RemoteChanges} from '$lib/classes/stores/remote-changes.svelte';
import {UserWordConverter} from '$lib/classes/data/user-word-converter';
import {compressor} from '$lib/classes/services/compressor';
import {WordConverter} from '$lib/classes/data/word-converter';
import {SentenceConverter} from '$lib/classes/data/sentence-converter';
import {LoggedIn} from '$lib/classes/stores/logged-in.svelte';
import {ToastManager} from '$lib/classes/stores/toast-manager.svelte';
import {UserActionProcessor} from '$lib/classes/services/user-action-processor';
import {TierManager} from '$lib/classes/state/tier-manager.svelte';
import {page} from '$app/state';
import {goto} from '$app/navigation';
import {protectRoute} from '$lib/helper-fns/protect-route';
import {getRelativeUrl} from '$lib/utils/get-relative-url';
import {Capacitor} from '@capacitor/core';
import {App as CapacitorApp} from '@capacitor/app';
import {SplashScreen} from '@capacitor/splash-screen';
import {AppError} from '$lib/classes/state/app-error.svelte';
import {ConnectionStatus} from '$lib/classes/state/connection-status.svelte.js';
import {ThemeManager} from '$lib/classes/services/theme-manager';
import {getDeviceIsReadyPromise} from '$lib/utils/get-device-is-ready-promise';
import {MobileUpdateManager} from '$lib/classes/manager/mobile-update-manager';
import {SentenceManager} from '$lib/classes/manager/sentence-manager';
import {UserWordManager} from '$lib/classes/manager/user-word-manager.svelte';
import {ASSETS_HOST, REVIEW_COUNT_MAX} from '../consts';
import {AudioPlayer} from '$lib/classes/manager/audio-player.svelte';
import {Device} from '$lib/classes/state/device.svelte';
import {IsPayingCustomer} from '$lib/classes/state/is-paying-customer.svelte';
import {AccessLevelState} from '$lib/classes/state/access-level-state.svelte';
import {WordCollection} from '$lib/classes/data/word-collection.svelte';
import {CollectionManager} from '$lib/data/collection-manager.js';

export const prerender = true //>> check for 'browser' necessary // also necessary with set to false
export const ssr = false // runs even though set to false >> depends on setting in pages? if 'ssr = true' there somewhere this needs to run here as well?

export async function load({url}) {
    // TODO wrap everything in (app) layout group and place an +error.svelte page outside
    //  so that errors happing here in layout.js will lead to that page
    // NOTE errors should be catched in setup and a suitable message can be displayed in the layout with `{#await} ... {:then} ... {:catch}`?
    // QUESTION should be fine for setup, but what about onNewUser, onLogin, onLogout errors?
    // these should also be propagated to setup error since called as part of auth.init()..?!?
    // maybe implement own logic for errors in this file and redirect to error page myself?

    const isNativePlatform = Capacitor.isNativePlatform()
    const appVersion = isNativePlatform ? (await CapacitorApp.getInfo()).version : ''
    const appId = isNativePlatform ? (await CapacitorApp.getInfo()).id : ''
    const platform = Capacitor.getPlatform()
    const deviceIsReadyPromise: Promise<void> = getDeviceIsReadyPromise(browser, platform)
    const sentryInstance = platform === 'web' ? Sentry : SentryCapacitor
    const mobileUpdateManager = new MobileUpdateManager(platform, isNativePlatform)

    logInProduction('------------')
    logInProduction('version', version, '>> platform', platform)

    if (isNativePlatform) {
        logInProduction('appId', appId, '>> appVersion', appVersion)
        await SplashScreen.show({autoHide: false})
    }

    if (browser && url.searchParams.has('ref')) {
        const refCode = url.searchParams.get('ref')!
        localStorage.setItem('ref', refCode)
    }

    // data
    // QUESTION better classes?
    // NOTE with Svelte 5 Data class possible with derived fields and everything encapsulated in one class
    // let allWords = []
    // let wordIdToWord = {}
    // let allSentences = []
    // let wordIdToSentences = {}
    // let sentenceIdToSentence = {}
    const wordCollection = new WordCollection()
    const sentenceManager = new SentenceManager()
    const verbConjugations = new VerbConjugations()
    const collectionManager = new CollectionManager()

    // converter
    const wordConverter = new WordConverter()
    const sentenceConverter = new SentenceConverter()
    const userWordConverter = new UserWordConverter()

    // services
    const firebaseApp = FirebaseApp // no class needed
    const database = new Database(firebaseApp, userWordConverter)
    const functions = new Functions(firebaseApp)

    const auth = new Auth(firebaseApp, isNativePlatform, SplashScreen, functions, onNewUser, onLogin, onLogout)

    // stores
    const loggedIn = new LoggedIn()
    const remoteChanges = new RemoteChanges(onLogout)
    const device = new Device(browser ? window.matchMedia : null)
    // state
    const connectionStatus = new ConnectionStatus((message) => sentryInstance.captureMessage(message, 'warning'))
    if (browser) connectionStatus.init()
    const toastManager = new ToastManager()
    const themeManager = new ThemeManager()
    const audioPlayer = new AudioPlayer(ASSETS_HOST)

    const userSettings = new UserSettings(database, themeManager)
    const userWordManager = new UserWordManager(database, userWordConverter, remoteChanges, wordIdToWord, sentenceManager, wordIdChanges, interval, functions)
    const proVersion = new ProVersion(database)
    const subscription = new Subscription(database, functions)
    //dynamic import to prevent error with import of revenueCat package and SSR
    const mobileSubscriptionManager = browser ? new ((await import('$lib/classes/services/mobile-subscription-manager.svelte')).MobileSubscriptionManager)(platform, appId, database, toastManager) : null
    const accessLevel = new AccessLevelState(
        () => loggedIn.current,
        () => proVersion.hasActiveTrial,
        () => proVersion.hasActivePaidProVersion,
        () => subscription.hasActiveSubscription,
        () => mobileSubscriptionManager?.activeMobileSubscription ?? false
    )
    const isPayingCustomer = new IsPayingCustomer(
        () => proVersion.hasActivePaidProVersion,
        () => subscription.hasActiveSubscription,
        () => mobileSubscriptionManager?.activeMobileSubscription ?? false
    )
    const paymentService = new PaymentService()
    const tierManager = new TierManager(paymentService, toastManager)
    const reviewCount = new ReviewCount(database, () => {})
    const freeLimitReached = new FreeLimitReached(() => accessLevel.current, () => reviewCount.current, REVIEW_COUNT_MAX)

    const userActionProcessor = new UserActionProcessor(accessLevel, toastManager, userWordManager)

    const appError = new AppError()

    //if adding User should make a problem because of web and native api >> introduce own User wrapper class!
    async function onNewUser() {
        sentryInstance.setTag('new_user', 'true');
        userWordManager.activateLoadPersistedUserWords()
    }

    async function onLogin(user: User) {
        console.log('onLogin >>')
        const timer_onLogin = timer('onLogin')
        const uid = user.uid

        try {
            sentryInstance.setUser({id: uid})
        } catch (error) {
            console.error('ERROR Sentry.setUser', error)
        }

        try {
            // NOTE any of these failing should block app
            // TODO check that thrown error in each function will be catched
            // noinspection ES6MissingAwait
            const promises = [
                userWordManager.init(uid),
                userSettings.init(user),
                reviewCount.init(uid),
                proVersion.init(user),
                subscription.connect(uid),
                mobileSubscriptionManager?.connect(uid),
            ]
            await Promise.all(promises)

            loggedIn.change(true)

            await protectRoute(page.url, page.url, true) //use page.url to prevent dependency on url parameter >> would cause rerun of the load function

        } catch (error) {
            appError.occured()
            throw new Error(`ERROR onLogin`, {cause: error})
        } finally {
            await SplashScreen.hide()
        }

        // NOTE shouldn't block app
        // noinspection ES6MissingAwait
        paymentService.authenticate(subscription.doc)

        try {
            // @ts-ignore
            window.Tawk_API?.setAttributes({jobTitle: user.uid}, (error: unknown) => {
                if (error === undefined) return
                console.error('ERROR tawk.setAttributes', error)
            });
        } catch (error) {
            // Who cares.
        }

        timer_onLogin()
    }

    async function onLogout() {
        console.log('onLogout >>')
        loggedIn.change(false)
        themeManager.reset()

        await protectRoute(page.url, page.url, false) //use page.url to prevent dependency on url parameter >> would cause rerun of the load function

        // TODO change to ONE wording close/clear/terminate/disconnect
        try {
            userWordManager.close()
            userSettings.clear()
            reviewCount.terminate()
            proVersion.close()
            subscription.disconnect()
            await paymentService.revokeAuthorization()
            await mobileSubscriptionManager?.disconnect()
        } catch (error) {
            appError.occured()
            console.error('ERROR onLogout', error)
        } finally {
            await SplashScreen.hide()
        }
    }

    const setupPromise = setup()

    async function setup() {
        if (!browser) return

        const timer_setup = timer('setup')
        let step = ''
        try {
            // NOTE needs to run concurrently because userWordManager needs data to validate saved userWords
            //  Errors loading data or initializing auth will be catched in AwaitSetup Comp (including errors of first run of onLogin)
            //  What happens if error in onLogin when opening page being logged out? -> Not thrown..? But still seems to block -> not logged in
            step = 'load data'
            const timer_data = timer('data')

            // TODO load data in parallel
            const [words, sentences] = await loadWordsSentences(compressor, wordConverter, sentenceConverter)

            wordCollection.setWords(words)
            setAllWords(words)
            initWordIdToWord(words)

            sentenceManager.setSentences(sentences)

            step = 'initialize verbConjugations'
            await verbConjugations.init()

            step = 'initialize collections'
            await collectionManager.init()

            step = 'initialize wordIdChanges'
            await loadWordIdChanges()

            timer_data()

            step = 'initialize Paddle' //already done here for Profitwell Retain
            const timer_paddle = timer('paddle')
            await paymentService.init()
            timer_paddle()

            step = 'initialize RevenueCat'
            await deviceIsReadyPromise
            await mobileSubscriptionManager?.init()

            step = 'initialize auth'
            const timer_auth = timer('auth')
            await auth.processSignInWithEmailLink(window.location.href)
            await auth.processRedirectResult()
            await auth.startAuthStateChangeListener()
            timer_auth()

        } catch (error) {
            appError.occured()
            throw new Error(`ERROR in ${step}`, {cause: error})
        }
        timer_setup()
        logInProduction('------------')
    }

    // Handle native App Links & App lifecycle events
    if (browser && isNativePlatform) {
        await CapacitorApp.addListener('appUrlOpen', async (event) => {
            const url = event.url
            const urlInstance = new URL(url)
            await setupPromise
            if (auth.isAuthLink(url)) {
                await auth.processSignInWithEmailLink(url)
            } else if (urlInstance.searchParams.has('email') && urlInstance.searchParams.has('pw')) {
                await SplashScreen.show({autoHide: false})
                const email = urlInstance.searchParams.get('email')!
                const pw = urlInstance.searchParams.get('pw')!
                try {
                    //ignore already authenticated case since only needed for Google android app testing crew
                    await auth.login(email, pw)
                } catch (error) {
                    console.error('ERROR signin in via searchParams', error)
                } finally {
                    await SplashScreen.hide()
                }
            } else {
                const navigateTo = getRelativeUrl(url)
                await goto(navigateTo)
            }
        })

        await CapacitorApp.addListener('appStateChange', ({isActive}) => {
            logInProduction('appStateChange - isActive', isActive);
            if (isActive) {
                mobileUpdateManager.reloadIfUpdateAvailable()
                mobileUpdateManager.checkForUpdates()
            }
        })
    }

    setupPromise.then(() => {
        mobileUpdateManager.checkForUpdates()
    })

    return {
        isNativePlatform: isNativePlatform,
        platform: platform,
        appError: appError,
        connectionStatus: connectionStatus,
        setup: setupPromise,
        database: database,
        allWords: allWords,
        wordCollection: wordCollection,
        wordIdToWord: wordIdToWord,
        sentenceManager: sentenceManager,
        functions: functions,
        auth: auth,
        loggedIn: loggedIn,
        remoteChanges: remoteChanges,
        device: device,
        userWordManager: userWordManager,
        verbConjugations: verbConjugations,
        collectionManager: collectionManager,
        reviewCount: reviewCount,
        freeLimitReached: freeLimitReached,
        userSettings: userSettings,
        proVersion: proVersion,
        subscription: subscription,
        isPayingCustomer: isPayingCustomer,
        paymentService: paymentService,
        tierManager: tierManager,
        mobileSubscriptionManager: mobileSubscriptionManager,
        toastManager: toastManager,
        audioPlayer: audioPlayer,
        userActionProcessor: userActionProcessor,
    }
}
